import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import usePaginationQuery from '@/hooks/usePaginationQuery';
import { useSearchParams } from 'react-router-dom';
import { getCustomerAddressAPI } from '@/src/apis/rest/address';
import { getRecyclingRequestsAPI } from '@/src/apis/rest/recyclingRequests';
import { getCreditAPI } from '@/src/apis/rest/creditHistory';
import { CreditHistoryType } from '@/src/models/CreditHistoryDto';
import { useQueries } from '@tanstack/react-query';
import TabTable from '@/src/pages/Customer/components/TabTable';
import {
  challengeColumn,
  customerAddressColumn,
  customerCommentColumn,
  customerCreditDeductionColumn,
  customerCreditExpiredColumn,
  customerCreditProvideColumn,
  customerCreditUseColumn,
  customerDonationColumn,
  customerEntryColumn,
  customerPostingColumn,
  customerRecyclingRequestColumn,
  customerMagazineColumn,
} from '@/src/pages/Customer/components/CustomerDetailTabTable/column';
import {
  MultipleTriggerProps,
  SingleTriggerProps,
} from '@/components/molecule/Tabs/types';
import { getCommunityPostingsAPI } from '@/src/apis/rest/communityPosting';
import { getCommunityCommentsAPI } from '@/src/apis/rest/communityComment';
import { getEntryParticipantsAPI } from '@/src/apis/rest/entry';
import { getDonationParticipantsAPI } from '@/src/apis/rest/donation';
import { getChallengeParticipantByIdAPI } from '@/src/apis/rest/challenge';
import { getMagazineCustomerHistoriesAPI } from '@/src/apis/rest/magazine';

enum TAB_KEY {
  ADDRESS = 'address',
  CREDIT = 'credit',
  CREDIT_PROVIDE = '적립',
  CREDIT_USE = '사용',
  CREDIT_EXPIRED = '소멸',
  CREDIT_DEDUCTION = '차감',
  RECYCLING_REQUEST = 'recyclingRequest',
  COMMUNITY = 'community',
  COMMUNITY_POSTING = 'communityPosting',
  COMMUNITY_COMMENT = 'communityComment',
  ENTRY = 'entry',
  DONATION = 'donation',
  CHALLENGE = 'challenge',
  MAGAZINE = 'magazine',
}

const DEFAULT_TAB = TAB_KEY.ADDRESS;

interface Props {
  customerId: string;
}

function CustomerDetailTabTable({ customerId }: Props) {
  const gridRef = useRef<AgGridReact>(null);
  const {
    page,
    pageSize,
    handlePageChange,
    reset: resetPage,
  } = usePaginationQuery();

  const tabTriggers = useMemo((): (
    | SingleTriggerProps
    | MultipleTriggerProps
  )[] => {
    return [
      { value: TAB_KEY.ADDRESS, label: '주소 목록' },
      {
        value: TAB_KEY.CREDIT,
        label: '얼스 크레딧 내역',
        optionTrigger: [
          {
            value: TAB_KEY.CREDIT_PROVIDE,
            label: '크레딧 적립 내역',
            isDefault: true,
          },
          { value: TAB_KEY.CREDIT_USE, label: '크레딧 사용 내역' },
          { value: TAB_KEY.CREDIT_EXPIRED, label: '크레딧 소멸 내역' },
          { value: TAB_KEY.CREDIT_DEDUCTION, label: '크레딧 차감 내역' },
        ],
      },
      { value: TAB_KEY.RECYCLING_REQUEST, label: '수거 신청 내역' },
      {
        value: TAB_KEY.COMMUNITY,
        label: '커뮤니티 내역',
        optionTrigger: [
          {
            value: TAB_KEY.COMMUNITY_POSTING,
            label: '게시글 내역',
            isDefault: true,
          },
          { value: TAB_KEY.COMMUNITY_COMMENT, label: '댓글 내역' },
        ],
      },
      {
        value: TAB_KEY.ENTRY,
        label: '참여한 응모 내역',
      },
      {
        value: TAB_KEY.DONATION,
        label: '참여한 기부 내역',
      },
      {
        value: TAB_KEY.CHALLENGE,
        label: '참여한 챌린지 내역',
      },
      {
        value: TAB_KEY.MAGAZINE,
        label: '조회한 매거진 내역',
      },
    ];
  }, []);

  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<TAB_KEY>(
    (searchParams.get('tabValue') as TAB_KEY) || DEFAULT_TAB,
  );

  const addressQueryFn = useCallback(() => {
    return getCustomerAddressAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const recyclingRequestQueryFn = useCallback(() => {
    return getRecyclingRequestsAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const creditQueryFn = useCallback(() => {
    return getCreditAPI({
      customerId: Number(customerId),
      type: selectedTab as CreditHistoryType,
      page,
      pageSize,
    });
  }, [customerId, page, selectedTab, pageSize]);

  const postingQueryFn = useCallback(() => {
    return getCommunityPostingsAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const commentQueryFn = useCallback(() => {
    return getCommunityCommentsAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const entryQueryFn = useCallback(() => {
    return getEntryParticipantsAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const donationQueryFn = useCallback(() => {
    return getDonationParticipantsAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const challengeQueryFn = useCallback(() => {
    return getChallengeParticipantByIdAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const magazineQueryFn = useCallback(() => {
    return getMagazineCustomerHistoriesAPI({
      customerId: Number(customerId),
      page,
      pageSize,
    });
  }, [customerId, page, pageSize]);

  const queriesResults = useQueries({
    queries: [
      {
        queryKey: ['customer-address', page, pageSize, customerId],
        queryFn: addressQueryFn,
        enabled: selectedTab === TAB_KEY.ADDRESS,
      },
      {
        queryKey: ['credit', selectedTab, page, pageSize, customerId],
        queryFn: creditQueryFn,
        enabled: [
          TAB_KEY.CREDIT_PROVIDE,
          TAB_KEY.CREDIT_USE,
          TAB_KEY.CREDIT_EXPIRED,
          TAB_KEY.CREDIT_DEDUCTION,
        ].includes(selectedTab),
      },
      {
        queryKey: ['recycling-request', page, pageSize, customerId],
        queryFn: recyclingRequestQueryFn,
        enabled: selectedTab === TAB_KEY.RECYCLING_REQUEST,
      },
      {
        queryKey: ['community-posting', page, pageSize, customerId],
        queryFn: postingQueryFn,
        enabled: selectedTab === TAB_KEY.COMMUNITY_POSTING,
      },
      {
        queryKey: ['community-comment', page, pageSize, customerId],
        queryFn: commentQueryFn,
        enabled: selectedTab === TAB_KEY.COMMUNITY_COMMENT,
      },
      {
        queryKey: ['entry', page, pageSize, customerId],
        queryFn: entryQueryFn,
        enabled: selectedTab === TAB_KEY.ENTRY,
      },
      {
        queryKey: ['donation', page, pageSize, customerId],
        queryFn: donationQueryFn,
        enabled: selectedTab === TAB_KEY.DONATION,
      },
      {
        queryKey: ['challenge', page, pageSize, customerId],
        queryFn: challengeQueryFn,
        enabled: selectedTab === TAB_KEY.CHALLENGE,
      },
      {
        queryKey: ['magazine', page, pageSize, customerId],
        queryFn: magazineQueryFn,
        enabled: selectedTab === TAB_KEY.MAGAZINE,
      },
    ],
  });

  const queryDataMap = useMemo(() => {
    return {
      [TAB_KEY.ADDRESS]: queriesResults[0],
      [TAB_KEY.CREDIT]: queriesResults[1],
      [TAB_KEY.CREDIT_PROVIDE]: queriesResults[1],
      [TAB_KEY.CREDIT_EXPIRED]: queriesResults[1],
      [TAB_KEY.CREDIT_USE]: queriesResults[1],
      [TAB_KEY.CREDIT_DEDUCTION]: queriesResults[1],
      [TAB_KEY.RECYCLING_REQUEST]: queriesResults[2],
      [TAB_KEY.COMMUNITY]: queriesResults[3],
      [TAB_KEY.COMMUNITY_POSTING]: queriesResults[3],
      [TAB_KEY.COMMUNITY_COMMENT]: queriesResults[4],
      [TAB_KEY.ENTRY]: queriesResults[5],
      [TAB_KEY.DONATION]: queriesResults[6],
      [TAB_KEY.CHALLENGE]: queriesResults[7],
      [TAB_KEY.MAGAZINE]: queriesResults[8],
    };
  }, [queriesResults]);

  const columnDefsMap = useMemo(() => {
    return {
      [TAB_KEY.ADDRESS]: customerAddressColumn(),
      [TAB_KEY.CREDIT]: customerCreditProvideColumn(),
      [TAB_KEY.CREDIT_PROVIDE]: customerCreditProvideColumn(),
      [TAB_KEY.CREDIT_EXPIRED]: customerCreditExpiredColumn(),
      [TAB_KEY.CREDIT_USE]: customerCreditUseColumn(),
      [TAB_KEY.CREDIT_DEDUCTION]: customerCreditDeductionColumn(),
      [TAB_KEY.RECYCLING_REQUEST]: customerRecyclingRequestColumn(),
      [TAB_KEY.COMMUNITY]: customerPostingColumn(),
      [TAB_KEY.COMMUNITY_POSTING]: customerPostingColumn(),
      [TAB_KEY.COMMUNITY_COMMENT]: customerCommentColumn(),
      [TAB_KEY.ENTRY]: customerEntryColumn(),
      [TAB_KEY.DONATION]: customerDonationColumn(),
      [TAB_KEY.CHALLENGE]: challengeColumn(),
      [TAB_KEY.MAGAZINE]: customerMagazineColumn(),
    };
  }, []);

  const handleTabChange = useCallback(
    (e: string) => {
      resetPage();
      setSelectedTab(e as TAB_KEY);
    },
    [resetPage],
  );

  useEffect(() => {
    if (!queryDataMap[selectedTab]) return;
    const { isFetching, isPending, isLoading } = queryDataMap[selectedTab];

    if ((isFetching || isLoading || isPending) && gridRef.current) {
      gridRef.current?.api?.showLoadingOverlay();
    }
  }, [queryDataMap, selectedTab]);

  return (
    <TabTable
      defaultTab={DEFAULT_TAB}
      onTabChange={handleTabChange}
      gridRef={gridRef}
      rowData={queryDataMap?.[selectedTab]?.data?.rows || []}
      isPagenation={selectedTab !== TAB_KEY.ADDRESS}
      page={page}
      pageSize={pageSize}
      handlePageChange={handlePageChange}
      totalCount={queryDataMap?.[selectedTab]?.data?.count || 0}
      isError={queryDataMap?.[selectedTab]?.isError}
      columnDefs={columnDefsMap?.[selectedTab] || []}
      tabTriggers={tabTriggers}
    />
  );
}

export default CustomerDetailTabTable;
